import {
  DecoratorOptionType,
  FilterTypes,
} from 'app/shared/componentsRestyled/coreDesignSystem/Table';
import _ from 'lodash';
import { nanoid } from 'nanoid';
import { createSelector } from 'reselect';

import { NUMBER_MONTHS_IN_YEAR } from 'constants/date';
import { FeatureFlag } from 'constants/featureFlags';
import {
  ALL_INVESTORS_OPTION,
  BEST_PRICING_INVESTOR_OPTION,
  BEST_PRICING_INVESTOR_VALUE,
  ProgramFilterType,
} from 'constants/programs';
import { getConditionalArray } from 'utils/data';
import { formattedYears } from 'utils/dayjs';
import { getDefaultLoanOptionFromDetails } from 'utils/programs';

import { selectFeatureFlagByKey } from 'store/global/selectors';

import { selectLoanApplication } from './loanApplication.selectors';
import { selectIsOBEngineType } from './loanApplicationCommonInfo.selectors';
import { selectOBFilterOptions } from './loanOption.selectors';
import { selectLoanProgramOptionsDetails } from './loanProgramOptionsDetails.selectors';

const selectLoanProgramOptions = createSelector(
  selectLoanApplication,
  ({ loanProgramOptions }) => loanProgramOptions,
);

export const selectLoanProgramOptionsGroupsWithDetails = createSelector(
  selectLoanProgramOptionsDetails,
  selectLoanProgramOptions,
  ({ programs, vendorType }, loanProgramOptions) => {
    if (!programs) {
      return [];
    }

    return programs.map(detailsItem => {
      const { programId } = detailsItem.program;
      const currentLoanProgramOptionsGroup = loanProgramOptions.filter(
        ({ programId: optionProgramId }) => optionProgramId === programId,
      );

      const loanProgramOptionsGroup = currentLoanProgramOptionsGroup.length
        ? currentLoanProgramOptionsGroup
        : [getDefaultLoanOptionFromDetails({ programDetails: detailsItem, vendorType })];

      // Par-first: the nearest-par option leads (index 0 is what the summary shows). Mirror the
      // engine's ZeroPoints bucket — prefer the par/credit side (points <= 0), then the smallest
      // magnitude. Keying on `points !== 0` broke once the engine stopped clamping the nearest-par
      // option's points to exactly 0, since it then surfaced the biggest credit instead.
      const orderedLoanProgramOptionsGroup = _.orderBy(
        loanProgramOptionsGroup,
        [loanOption => loanOption.points > 0, loanOption => Math.abs(loanOption.points)],
        ['asc', 'asc'],
      );

      return {
        programId,
        key: nanoid(),
        programDetails: detailsItem,
        loanProgramOptionsGroup: orderedLoanProgramOptionsGroup,
      };
    });
  },
);

const selectLoanProgramsFilterOptions = createSelector(
  selectLoanProgramOptions,
  selectLoanProgramOptionsDetails,
  (loanProgramOptions, { programs }) => {
    return {
      loanType: _.uniq(programs?.map(({ program }) => program.programLoanType) ?? [])
        .sort()
        .map(loanType => ({ value: loanType, label: loanType })),
      amortizationTypes: _.uniq(loanProgramOptions?.map(option => option.amortizationType) ?? [])
        .sort()
        .map(amortType => ({ value: amortType, label: amortType })),
      loanTerm: _.uniq(loanProgramOptions?.map(option => option.loanPeriod) ?? [])
        .sort((a, b) => a - b)
        .map(termValue => ({
          value: termValue,
          label: formattedYears(Number((termValue / NUMBER_MONTHS_IN_YEAR).toFixed(2))),
        })),
      lockTerm: _.uniq(programs?.map(({ selectedRateLockTerm }) => selectedRateLockTerm) ?? [])
        .sort((a, b) => a - b)
        .map(lockTerm => ({ value: lockTerm, label: `${lockTerm} Days` })),
    };
  },
);

export const selectLoanProgramsFilterDefs = createSelector(
  [
    selectIsOBEngineType,
    selectLoanProgramsFilterOptions,
    selectOBFilterOptions,
    selectFeatureFlagByKey(FeatureFlag.DeduplicateRemoteLoanOptionsOnGroups),
  ],
  (isOBEngineType, loanProgramsFilterOptions, obFilterOptions, isOptionsDeduplicateEnabled) => {
    const searchFilterOptions = isOBEngineType ? obFilterOptions : loanProgramsFilterOptions;

    return [
      {
        filterId: ProgramFilterType.LOAN_TYPE,
        label: 'Loan Type',
        isClearable: true,
        selectOptions: searchFilterOptions.loanType,
        width: 130,
      },
      {
        filterId: ProgramFilterType.AMORTIZATION_TYPES,
        label: 'Fixed',
        isClearable: true,
        selectOptions: searchFilterOptions.amortizationTypes,
        width: 95,
      },
      {
        filterId: ProgramFilterType.LOAN_TERM,
        label: 'Loan Term',
        isClearable: true,
        selectOptions: searchFilterOptions.loanTerm,
        width: 132,
      },
      {
        filterId: ProgramFilterType.LOCK_TERM,
        label: 'Lock Term',
        filterType: FilterTypes.SINGLE_SELECT,
        isClearable: true,
        selectOptions: searchFilterOptions.lockTerm,
        width: 132,
      },
      ...getConditionalArray(isOBEngineType && isOptionsDeduplicateEnabled, () => [
        {
          filterId: ProgramFilterType.INVESTOR,
          label: 'Investor',
          filterType: FilterTypes.MULTI_SELECT,
          defaultValue: [BEST_PRICING_INVESTOR_VALUE],
          selectOptions: [
            BEST_PRICING_INVESTOR_OPTION,
            { value: DecoratorOptionType.DIVIDER },
            ...getConditionalArray(obFilterOptions.investorIds.length > 1, [ALL_INVESTORS_OPTION]),
            ...obFilterOptions.investorIds,
          ],
          width: 160,
        },
      ]),
    ];
  },
);
